export const FUSIONNOW_BASE_URL =
  process.env.REACT_APP_FUSIONNOW_BASE_URL ||
  'https://staging.apimac.momentumamp.com';

export class FusionNowNotSubscribedError extends Error {
  constructor() {
    super('Not a MAC subscriber');
    this.name = 'FusionNowNotSubscribedError';
  }
}

interface MacToken {
  token: string;
  subscribed: boolean;
}

let _cachedToken: MacToken | null = null;
let _tokenExpiry: number | null = null;
// In-flight promise guard — parallel callers (workflows, recipes, analytics on
// the same page) share one mac-token request.
let _pendingToken: Promise<MacToken> | null = null;

export function clearFusionNowTokenCache(): void {
  _cachedToken = null;
  _tokenExpiry = null;
  _pendingToken = null;
}

// ── MAC JWT ───────────────────────────────────────────────────────────────────
//
// Issued by the Hub BE against the user's own session — replaces the old
// unauthenticated MAC agent_token.json?agent_id= call.
//
//   GET {Auth_Url}/api/account/mac-token?nctoken=<amsToken>
//   Authorization: Bearer <authToken>
//   → { success: true, token: "<MAC JWT>", subscription_info: "active" }
//
// Non-MAC users also get a token (subscription_info: null). MAC rejects it on
// user-scoped endpoints but accepts it on the global marketplace endpoints.
// ─────────────────────────────────────────────────────────────────────────────

async function getMacToken(): Promise<MacToken> {
  if (_cachedToken && _tokenExpiry && Date.now() < _tokenExpiry - 60_000) {
    return _cachedToken;
  }

  if (_pendingToken) return _pendingToken;

  _pendingToken = (async (): Promise<MacToken> => {
    try {
      const amsToken = localStorage.getItem('amsToken');
      const authToken = localStorage.getItem('authToken');
      if (!amsToken || !authToken)
        throw new Error('amsToken or authToken not found in localStorage');

      const res = await fetch(
        `${process.env.REACT_APP_Auth_Url}/api/account/mac-token?nctoken=${encodeURIComponent(amsToken)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // Only subscription_info "active" (any case) is a MAC user — any other
      // value, a missing field, or no parseable body (any status) is non-MAC
      const data = await res.json().catch(() => null);
      const subscribed =
        String(data?.subscription_info ?? '').trim().toLowerCase() === 'active';
      if (!res.ok || !data?.success || !data.token) {
        if (!subscribed) throw new FusionNowNotSubscribedError();
        throw new Error(`MAC token request failed: ${res.status}`);
      }

      try {
        const payload = JSON.parse(
          atob(data.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
        );
        _tokenExpiry = payload.exp ? payload.exp * 1000 : Date.now() + 3_600_000;
      } catch {
        _tokenExpiry = Date.now() + 3_600_000;
      }

      _cachedToken = { token: data.token, subscribed };
      return _cachedToken;
    } finally {
      _pendingToken = null;
    }
  })();

  return _pendingToken;
}

// MAC subscribers only — user-scoped endpoints (my workflows/recipes,
// analytics, clone, AI chat).
export async function getFusionNowToken(): Promise<string> {
  const { token, subscribed } = await getMacToken();
  if (!subscribed) throw new FusionNowNotSubscribedError();
  return token;
}

// Any signed-in user — global marketplace endpoints (public workflows,
// community recipes, apps).
export async function getMacMarketplaceToken(): Promise<string> {
  return (await getMacToken()).token;
}

// ── MAC AI Chat token ─────────────────────────────────────────────────────────
//
// auth0_config is a BE proxy — it receives our FusionNow JWT, performs the
// Auth0 client_credentials exchange server-side, and returns the final
// access_token directly. No client_secret ever touches the frontend.
//
// Confirmed BE response shape:
//   { access_token: string, expires_in: 86400, token_type: "Bearer", scope: string }
// ─────────────────────────────────────────────────────────────────────────────

export const MAC_AI_API_BASE =
  process.env.REACT_APP_MAC_AI_API_BASE ||
  'https://mac.api.fusionnowcrm.com/api/v1';

let _macAiToken: string | null = null;
let _macAiTokenExpiry: number | null = null;
// In-flight promise guard — prevents duplicate auth0_config fetches when
// multiple callers race before the first response is cached.
let _pendingMacAiToken: Promise<string> | null = null;

export function clearMacAIChatTokenCache(): void {
  _macAiToken = null;
  _macAiTokenExpiry = null;
  // Also null the in-flight ref — prevents a concurrent fetch from
  // resurrecting the cache after an explicit clear (e.g. on logout).
  _pendingMacAiToken = null;
}

export async function getMacAIChatToken(): Promise<string> {
  // Return cached token if still valid (with 60s buffer before expiry)
  if (
    _macAiToken &&
    _macAiTokenExpiry &&
    Date.now() < _macAiTokenExpiry - 60_000
  ) {
    return _macAiToken;
  }

  // Return the same in-flight promise to all concurrent callers —
  // prevents multiple simultaneous auth0_config requests.
  if (_pendingMacAiToken) return _pendingMacAiToken;

  _pendingMacAiToken = (async (): Promise<string> => {
    try {
      // Step 1: FusionNow JWT (reuses its own cache + subscription check)
      const fnToken = await getFusionNowToken();

      // Step 2: Exchange FN token for MAC AI access_token via BE proxy
      const res = await fetch(
        `${FUSIONNOW_BASE_URL}/api/open_api/v1/auth0_config`,
        { headers: { Authorization: `Bearer ${fnToken}` } }
      );
      if (!res.ok) throw new Error(`auth0_config failed: ${res.status}`);

      const { access_token, expires_in } = await res.json();
      if (!access_token)
        throw new Error('auth0_config: missing access_token in response');

      // Cache — prefer JWT exp claim for precision, fall back to expires_in (86400 = 24h)
      try {
        const payload = JSON.parse(
          atob(access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
        );
        _macAiTokenExpiry = payload.exp
          ? payload.exp * 1000
          : Date.now() + (expires_in ?? 86400) * 1000;
      } catch {
        _macAiTokenExpiry = Date.now() + (expires_in ?? 86400) * 1000;
      }

      _macAiToken = access_token;
      return _macAiToken as string;
    } finally {
      // Always clear the in-flight ref so the next call can proceed normally
      _pendingMacAiToken = null;
    }
  })();

  return _pendingMacAiToken;
}
